/// <amd-module name="Build/Admin/AgentCloudsList/Components/AgentCloudsHub" />
import * as React from "react";

import { caseInsensitiveContains } from "VSS/Core/Util/String";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";

import { Card } from "VSSUI/Card";
import { FilterBar } from "VSSUI/FilterBar";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem, getFilterItem } from "VSSUI/HeaderCommandBar";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Filter, FILTER_CHANGE_EVENT } from "VSSUI/Utilities/Filter";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";

import { TaskAgentCloud } from "DistributedTask/Client/Contracts/TaskAgent";

import { AgentCloudsListActionCreator } from "Build/Admin/AgentCloudsList/Actions/AgentCloudsListActionCreator";
import { AgentCloudsList } from "Build/Admin/AgentCloudsList/Components/AgentCloudsList";
import { CreateAgentCloudPanel } from "Build/Admin/AgentCloudsList/Components/CreateAgentCloudPanel";
import { StoresHub } from "Build/Admin/AgentCloudsList/Stores/StoresHub";
import { NotificationArea } from "Build/Admin/Common/Components/NotificationArea";
import { Assets, FilterKeys } from "Build/Admin/Common/Constants";
import { INotification } from "Build/Admin/Common/Stores/NotificationStore";

import * as Resources from "Build/Admin/AgentCloudsList/Resources";

export interface IAgentCloudsHubProps {
    actionCreator: AgentCloudsListActionCreator;
    storesHub: StoresHub;
}

export interface IAgentCloudsHubState {
    agentClouds: TaskAgentCloud[];
    createPanelIsOpen: boolean;
    filterApplied: boolean;
    nameFilter: Filter;
    notifications: INotification[];
    showFilterBar: boolean;
}

export class AgentCloudsHub extends VssComponent<IAgentCloudsHubProps, IAgentCloudsHubState> {

    constructor(props: IAgentCloudsHubProps, context: IVssComponentContext) {
        super(props, context);
        const nameFilter: Filter = new Filter();
        this.state = this._getStateFromStores(nameFilter, false, false);
    }

    public componentDidMount(): void {
        this.props.storesHub.agentCloudStore.addChangedListener(this._onStoreChanged);
        this.props.storesHub.notificationStore.addChangedListener(this._onStoreChanged);
        this.state.nameFilter.subscribe(this._onStoreChanged, FILTER_CHANGE_EVENT);
    }

    public componentWillUnmount(): void {
        this.props.storesHub.agentCloudStore.removeChangedListener(this._onStoreChanged);
        this.props.storesHub.notificationStore.removeChangedListener(this._onStoreChanged);
        this.state.nameFilter.unsubscribe(this._onStoreChanged, FILTER_CHANGE_EVENT);
    }

    public render(): JSX.Element {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow">
                    <Header title={Resources.AgentClouds} titleSize={TitleSize.Large} commandBarItems={this._getCommandBarItems()} />
                    {
                        this.state.showFilterBar &&
                        <FilterBar filter={this.state.nameFilter} onDismissClicked={() => this._toggleFilterBar(true)} >
                            <KeywordFilterBarItem filterItemKey={FilterKeys.Name} />
                        </FilterBar>
                    }
                    <NotificationArea notifications={this.state.notifications} onNotificationDismissed={this._onNotificationDismissed} />
                    <div className="page-content page-content-top">
                    {
                        this._shouldRenderZeroData() &&
                        <ZeroData
                            primaryText={Resources.ZeroAgentCloudsPrimaryText}
                            secondaryText={Resources.ZeroAgentCloudsSecondaryText}
                            actionType={ZeroDataActionType.ctaButton}
                            actionText={Resources.AddAgentCloud}
                            onActionClick={() => {}}
                            imagePath={getAssetLocation(this.context.pageContext, Assets.ZeroDataPng)}
                            imageAltText="" />
                    }
                    {
                        !this._shouldRenderZeroData() &&
                        <Card className="flex-grow bolt-card-no-vertical-padding" contentProps={{ contentPadding: false }}>
                            <AgentCloudsList
                                agentClouds={this.state.agentClouds}
                                onDeleteAgentCloud={this._confirmDeleteAgentCloud}
                                pageContext={this.context.pageContext} />
                        </Card>
                    }
                    </div>
                </Page>
                <CreateAgentCloudPanel
                    actionCreator={this.props.actionCreator}
                    isOpen={this.state.createPanelIsOpen}
                    onDismiss={() => this.setState({createPanelIsOpen: !this.state.createPanelIsOpen})}
                    />
            </Surface>
        );
    }

    private _shouldRenderZeroData(): boolean {
        return this.props.storesHub.agentCloudStore.state.length === 0;
    }

    private _getCommandBarItems(): IHeaderCommandBarItem[] {
        const commandBarItems: IHeaderCommandBarItem[] = [];

        if (!this._shouldRenderZeroData()) {
            commandBarItems.push(
                getFilterItem(
                    () => {
                        this._toggleFilterBar(false);
                    },
                    this.state.filterApplied
                )
            );
        }

        commandBarItems.push(
            {
                text: Resources.AddAgentCloud,
                id: "addAgentCloud",
                isPrimary: true,
                onActivate: () => {
                    this.setState({createPanelIsOpen: true})
                }
            }
        );

        return commandBarItems;
    }

    private _toggleFilterBar(resetFilter: boolean): void {
        if (resetFilter) {
            this.state.nameFilter.reset();
        }

        this.setState({
            showFilterBar: !this.state.showFilterBar
        });
    }

    private _onNotificationDismissed = (notification: INotification): void => {
        this.props.actionCreator.notificationActionCreator.dismissNotification(notification);
    }

    private _confirmDeleteAgentCloud = (agentCloud: TaskAgentCloud): void => {
        this._deleteAgentCloud(agentCloud);
    }

    private _deleteAgentCloud = (agentCloud: TaskAgentCloud): void => {
        this.props.actionCreator.deleteAgentCloud(agentCloud);
    }

    private _onStoreChanged = (): void => {
        this.setState(this._getStateFromStores(this.state.nameFilter, this.state.showFilterBar, this.state.createPanelIsOpen));
    }

    private _getStateFromStores(nameFilter: Filter, showFilterBar: boolean, createPanelIsOpen: boolean): IAgentCloudsHubState {
        const nameFilterValue: string | undefined = nameFilter.getFilterItemValue(FilterKeys.Name);

        return {
            agentClouds: this._getFilteredAgentClouds(this.props.storesHub.agentCloudStore.state, nameFilterValue),
            createPanelIsOpen,
            filterApplied: !!nameFilterValue,
            nameFilter,
            notifications: this.props.storesHub.notificationStore.state,
            showFilterBar
        };
    }

    private _getFilteredAgentClouds(agentClouds: TaskAgentCloud[], nameFilterValue: string | undefined): TaskAgentCloud[] {
        if (nameFilterValue) {
            return agentClouds.filter(agentCloud => agentCloud && caseInsensitiveContains(agentCloud.name, nameFilterValue));
        }
        else {
            return agentClouds;
        }
    }
}
